import { PageLoader } from '@zeiss/lib-components';
import * as React from 'react';
import { useTranslation } from 'react-i18next';

import {
  AppConfig,
  AppConfigProviderProps,
  AppConfigSchema,
  AppConfigState,
} from 'src/config/types';
import { APP_CONFIG_STORAGE_KEY, APP_CONFIG_URL } from 'src/constants';

// eslint-disable-next-line react-refresh/only-export-components
export const AppConfigContext = React.createContext<AppConfig | undefined>(
  undefined,
);

export const AppConfigProvider = ({ children }: AppConfigProviderProps) => {
  const { t } = useTranslation(['common']);

  const [state, setState] = React.useState<AppConfigState>({
    status: 'loading',
  });

  React.useEffect(() => {
    const fetchConfig = async () => {
      try {
        const response = await fetch(APP_CONFIG_URL);

        const rawData = await response.json();
        const config = AppConfigSchema.parse(rawData);

        /**
         * Store config in localStorage to make it available outside AppConfigContext
         * e.g in helpers etc
         */
        localStorage.setItem(APP_CONFIG_STORAGE_KEY, JSON.stringify(config));

        setState({
          config,
          status: 'success',
        });
      } catch (err) {
        console.error('Error initializing application config:', err);

        setState({
          error:
            err instanceof Error
              ? err.message
              : 'Unable to fetch application config',
          status: 'error',
        });
      }
    };

    fetchConfig();
  }, []);

  if (state.status === 'loading') {
    return <PageLoader label={t('common:loadingFeedback.loading')} size="xl" />;
  }

  if (state.status === 'error') {
    return <div>Error loading data</div>;
  }

  return (
    <AppConfigContext.Provider value={state.config}>
      {children}
    </AppConfigContext.Provider>
  );
};
